import { useState } from 'react';
import { SimpleForm, Button, SaveButton, useTranslate, Confirm } from 'react-admin';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { Update } from '@mui/icons-material';
import { useUpdateManyController } from '@/composables/useUpdateManyController';
import PropTypes from 'prop-types';
import { Palette } from '@/themes/themeOverrides/Palette';
import { CustomConfirmContent } from '@/components/global/buttons/CustomConfirmContent.jsx';
import { globalStyles } from '@/themes/commonStyles/index.js';

export default function FormDialog({ title, children, startIcon, customClass = '', transform = null, confirmTitle, confirmMessage }) {
    const { open, isPending, handleOpen, handleClose, handleSubmit } = useUpdateManyController({ transform });
    const translate = useTranslate();
    const buttonIcon = startIcon ?? <Update />;

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingData, setPendingData] = useState(null);
    const needsConfirmation = Boolean(confirmTitle || confirmMessage);

    const submitDirectly = data => {
        handleSubmit(data);
    };

    const submitWithConfirmation = data => {
        setPendingData(data);
        setConfirmOpen(true);
    };

    const handleFormSubmit = needsConfirmation ? submitWithConfirmation : submitDirectly;

    const handleConfirm = () => {
        setConfirmOpen(false);
        handleClose();
        handleSubmit(pendingData);
    };

    const handleDialogClose = () => {
        setConfirmOpen(false);
        setPendingData(null);
    };

    return (
        <>
            <Button onClick={handleOpen} startIcon={buttonIcon} label={title} />
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth={true}
                className={`bulk-dialog ${customClass}`}
                sx={{
                    '& .MuiPaper-root.MuiDialog-paper': {
                        overflowY: 'auto',
                    },
                    '& .MuiPaper-root.MuiPaper-elevation': {
                        borderRadius: '32px',
                        padding: '40px',
                        boxShadow: Palette.boxShadow.elevation3,
                    },
                    '& .MuiDialogContent-root': {
                        padding: '0 !important',
                        maxWidth: '400px !important',
                    },
                    '& .MuiTypography-root.MuiTypography-h6': {
                        padding: 0,
                        marginBottom: '24px',
                    },
                    '& .MuiCardContent-root': {
                        padding: '0',
                    },
                    '& .MuiAutocomplete-root.MuiAutocomplete-fullWidth': {
                        minWidth: '100%',
                    },
                    '& .MuiPaper-root': {
                        overflow: 'visible',
                        '& .MuiAutocomplete-paper': {
                            padding: '13px',
                            borderRadius: '12px',
                        },
                        '& .MuiFormHelperText-root': {
                            fontSize: '11px',
                            '&.Mui-disabled': {
                                marginBottom: '20px',
                            },
                        },
                    },
                    '&.bulk-dialog--access-key': {
                        '& .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiDialog-paper': {
                            width: 'auto !important',
                        },
                        '& .rs-picker.rs-picker-date': {
                            marginBottom: '20px',
                        },
                    },
                }}
            >
                <DialogTitle fontSize='24px' fontWeight='bold'>
                    {title}
                </DialogTitle>
                <SimpleForm id='DialogSimpleForm' onSubmit={handleFormSubmit} mode='onBlur' reValidateMode='onBlur' toolbar={null}>
                    {children}
                    <SaveButton disabled={isPending} label={translate('common.save')} icon={null} />
                </SimpleForm>
            </Dialog>
            {needsConfirmation && (
                <Confirm
                    isOpen={confirmOpen}
                    title={confirmTitle ?? title}
                    content={
                        <CustomConfirmContent
                            content={confirmMessage ?? translate('common.confirmTitle')}
                            handleDialogClose={handleDialogClose}
                            handleConfirm={handleConfirm}
                            loading={isPending}
                        />
                    }
                    onConfirm={handleConfirm}
                    onClose={handleDialogClose}
                    sx={{ ...globalStyles.customConfirmDialog }}
                />
            )}
        </>
    );
}

FormDialog.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node,
    startIcon: PropTypes.node,
    customClass: PropTypes.string,
    transform: PropTypes.func,
    confirmTitle: PropTypes.string,
    confirmMessage: PropTypes.string,
};
